/**
 * embedded.js（子側）の実行モード検出
 *
 * 起動時のコンテキスト（親フレームの有無、親 bridge の応答）を判定し、
 * 3 つのモードに分類する：
 *
 *   - direct          : window.self === window.top → 親ページに直接埋め込み（WordPress 等）
 *   - bridge          : iframe 内 + 親 ack 受信 → クリック時 postMessage で親に委譲
 *   - inline-fallback : iframe 内 + 親 ack 受信なし → 従来通り iframe 内モーダル
 *
 * 子側受信メッセージの origin について：
 * 親（parent-bridge.js が動く顧客サイト）の origin は STUDIO Preview / 各顧客の
 * 公開ドメインなど多岐にわたるため、子側ではホワイトリストでのチェックを行わない。
 * 代わりに「メッセージ形式（type/version/instanceId）」と「自分の instanceId 宛か」で
 * 偽装を弾く。仮に偽 ack を受信しても、bridge mode で送信される moovy:open は
 * scenario/tenant/video の ID のみで機微情報を含まないため、実害は限定される。
 */

import {
  MESSAGE_TYPES,
  PROTOCOL_VERSION,
  isCompatibleVersion,
  isMoovyMessage,
  type OpenPayload,
  type PingMessage,
  type OpenMessage,
  type PrepareMessage,
  type PreparePayload,
} from './protocol'

export type EmbeddedMode = 'direct' | 'bridge' | 'inline-fallback'

/**
 * iframe 内 embedded.js が ping → ack を待つタイムアウト。
 *
 * 現実的な postMessage roundtrip + parent-bridge.js のリスナー処理は通常 12〜70ms。
 * 100ms あれば 95% 以上のケースで余裕を持って ack を受け取れる推定値。
 *
 * タイムアウトしても致命傷にはならない: PR #93 の `listenForLateAck` と
 * PR #94 の `broadcastBridgeHello` により、parent-bridge.js が遅延ロード
 * された場合も自動で bridge mode に再アップグレードされる。
 *
 * 200ms から 100ms に短縮した経緯: inline-fallback で起動するユーザーの
 * Script→サムネイル描画区間を 100ms 短縮するため（観点1のパフォーマンス改善）。
 */
export const DEFAULT_HANDSHAKE_TIMEOUT_MS = 100

export type DetectModeOptions = {
  win?: Window
  timeoutMs?: number
  instanceId: string
}

/**
 * 親に向けて ping を送信する内部ヘルパー。
 */
const sendPing = (instanceId: string, win: Window) => {
  const ping: PingMessage = {
    type: MESSAGE_TYPES.PING,
    version: PROTOCOL_VERSION,
    instanceId,
  }
  win.parent.postMessage(ping, '*')
}

/**
 * モード検出。direct なら同期的に確定。iframe 内なら ping を送って ack を待つ（async）。
 *
 * 親→子の bridge-hello を受信した場合は、ping を再送して ack 受信のチャンスを作る。
 */
export const detectMode = (options: DetectModeOptions): Promise<EmbeddedMode> => {
  const win = options.win ?? window
  const timeoutMs = options.timeoutMs ?? DEFAULT_HANDSHAKE_TIMEOUT_MS

  if (win.top === win.self) return Promise.resolve('direct')

  return new Promise<EmbeddedMode>((resolve) => {
    let settled = false

    const cleanup = () => {
      win.removeEventListener('message', onMessage)
    }

    const onMessage = (event: MessageEvent) => {
      // 防御的ガード：cleanup 後に listener が呼ばれることは構造上無いが念のため
      /* v8 ignore next */
      if (settled) return
      if (!isMoovyMessage(event.data)) return
      if (!isCompatibleVersion(event.data.version)) return

      // 親が「準備できた」と broadcast してきた場合は ping を再送する
      if (event.data.type === MESSAGE_TYPES.BRIDGE_HELLO) {
        sendPing(options.instanceId, win)
        return
      }

      if (event.data.type !== MESSAGE_TYPES.ACK) return
      if (event.data.instanceId !== options.instanceId) return

      settled = true
      cleanup()
      resolve('bridge')
    }

    win.addEventListener('message', onMessage)

    sendPing(options.instanceId, win)

    win.setTimeout(() => {
      if (settled) return
      settled = true
      cleanup()
      resolve('inline-fallback')
    }, timeoutMs)
  })
}

/**
 * bridge mode で「動画を開きたい」と親に通知する postMessage を送信する。
 */
export const sendOpenIntent = (
  payload: OpenPayload,
  instanceId: string,
  win: Window = window,
): void => {
  const message: OpenMessage = {
    type: MESSAGE_TYPES.OPEN,
    version: PROTOCOL_VERSION,
    instanceId,
    payload,
  }
  win.parent.postMessage(message, '*')
}

/**
 * 案E: hidden pre-mount を親にお願いするための事前通知。
 *
 * iframe 側 EmbeddingAiInterview の SWR が解決した時点で送る。
 * 親はこのメッセージを受けて scenario/tenant データを SWR cache に preload し、
 * EmbeddingAiInterview を hidden で mount しておく（重い React mount を
 * クリック前の余裕時間に倒すため）。
 *
 * - PREPARE 自体には機微情報を含まないため targetOrigin='*' で安全
 * - 旧 parent-bridge.js は受信しても無視するだけ（後方互換性あり）
 */
export const sendPrepareIntent = (
  payload: PreparePayload,
  instanceId: string,
  win: Window = window,
): void => {
  const message: PrepareMessage = {
    type: MESSAGE_TYPES.PREPARE,
    version: PROTOCOL_VERSION,
    instanceId,
    payload,
  }
  win.parent.postMessage(message, '*')
}

export const DEFAULT_LATE_ACK_DURATION_MS = 30000

export type ListenForLateAckOptions = {
  win?: Window
  instanceId: string
  durationMs?: number
  onAck: () => void
}

/**
 * 初回 detectMode のタイムアウト（DEFAULT_HANDSHAKE_TIMEOUT_MS）後に遅れて到着する
 * ack / bridge-hello を待ち受ける。
 *
 * 用途：parent-bridge.js が </body> 末尾や Custom Code 経由で遅延ロードされ、
 * iframe 内 embedded.js のハンドシェイクタイムアウト後に parent-bridge.js が
 * 準備完了するケース。
 *
 * - bridge-hello 受信時：ping を再送して ack を促す（リスナーは継続）
 * - ack 受信時：onAck コールバックを呼んでクリーンアップ
 *
 * @returns 監視を中止するための cancel 関数（idempotent）
 */
export const listenForLateAck = (
  options: ListenForLateAckOptions,
): { cancel: () => void } => {
  const win = options.win ?? window
  const durationMs = options.durationMs ?? DEFAULT_LATE_ACK_DURATION_MS
  let settled = false

  const onMessage = (event: MessageEvent) => {
    // 防御的ガード：cleanup 後に listener が呼ばれることは構造上無いが念のため
    /* v8 ignore next */
    if (settled) return
    if (!isMoovyMessage(event.data)) return
    if (!isCompatibleVersion(event.data.version)) return

    // 親が「準備できた」と broadcast してきた場合は ping を再送する（ack を促す）
    if (event.data.type === MESSAGE_TYPES.BRIDGE_HELLO) {
      sendPing(options.instanceId, win)
      return
    }

    if (event.data.type !== MESSAGE_TYPES.ACK) return
    if (event.data.instanceId !== options.instanceId) return

    settled = true
    win.removeEventListener('message', onMessage)
    win.clearTimeout(timeoutId)
    options.onAck()
  }

  win.addEventListener('message', onMessage)

  const timeoutId = win.setTimeout(() => {
    /* v8 ignore next */
    if (settled) return
    settled = true
    win.removeEventListener('message', onMessage)
  }, durationMs)

  return {
    cancel: () => {
      if (settled) return
      settled = true
      win.removeEventListener('message', onMessage)
      win.clearTimeout(timeoutId)
    },
  }
}
